import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as yup from 'yup';
import { Option, SUPPORTED_LEAGUES } from '../../../../types';
import { useStringsContext } from '../../../../contexts/strings';
import SelectWithValidate, { SelectOptions } from '../../../common/input/select-with-validate';
import { FS_PATH_APP_CONFIGS } from '../../../../constants';
import firebaseApp from '../../../../firebase-app';
import { collection, limit, query, where, getDocs } from 'firebase/firestore';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import svStrings from '../../../../sv-strings';
import { InputWithValidateProps } from '../../../common/input/text-input-with-validate';
import Typography from '@mui/material/Typography';
import { useSquad } from '../../squad-database/hooks/squad';

const { REACT_APP_DEFAULT_LEAGUE_SEASON } = process.env;

const playerSelection = yup
  .string()
  .nullable()
  .required(svStrings.validations.mustSelectAlternative);

const teamSelection = yup.string().nullable().required(svStrings.validations.mustSelectAlternative);

interface Props extends Omit<InputWithValidateProps, 'onValue'> {
  selectedAbbrv: string;
  onSelectedPlayers: (selectedPlayers: SelectOptions | null) => void;
  initialSelectedPlayers?: SelectOptions;
}

const PollSelectedPlayersLeague = ({
  selectedAbbrv,
  onValidation,
  hideValidationError,
  onSelectedPlayers,
  initialSelectedPlayers,
}: Props) => {
  const { strings } = useStringsContext();
  const { fetchSquad } = useSquad();
  const initSelectedPlayers = useRef(false);

  const [squadLoading, setSquadLoading] = useState(false);

  const [playersOptions, setPlayersOptions] = useState<SelectOptions>({
    options: [],
    optionsText: [],
  });

  const [selectedPlayers, setSelectedPlayers] = useState<SelectOptions>({
    options: [],
    optionsText: [],
  });

  const [teamsList, setTeamsList] = useState<SelectOptions>({
    options: [],
    optionsText: [],
  });

  const [selectedTeamAbbrv, setSelectedTeamAbbrv] = useState<string | null>(null);

  useEffect(() => {
    if (initialSelectedPlayers && !initSelectedPlayers.current) {
      setSelectedPlayers(initialSelectedPlayers);
      onSelectedPlayers(initialSelectedPlayers);
      initSelectedPlayers.current = true;
    }
  }, [initialSelectedPlayers, onSelectedPlayers]);

  useEffect(() => {
    const getSquad = async () => {
      if (selectedTeamAbbrv) {
        setSquadLoading(true);
        try {
          const playerSquad = await fetchSquad(
            selectedTeamAbbrv,
            Number(REACT_APP_DEFAULT_LEAGUE_SEASON),
            false,
            false
          );
          if (playerSquad) {
            setPlayersOptions({
              options: playerSquad.map((player) => String(player.fogisId)),
              optionsText: playerSquad.map(
                (player) => player.displayName || String(player.fogisId)
              ),
            });
          }
        } catch (error) {
          // Do nothing
        }
        setSquadLoading(false);
      }
    };
    getSquad();
  }, [fetchSquad, selectedAbbrv, selectedTeamAbbrv]);

  useEffect(() => {
    const getTeams = async () => {
      const league = selectedAbbrv as keyof typeof SUPPORTED_LEAGUES;
      const colRef = collection(firebaseApp.db, FS_PATH_APP_CONFIGS);
      const docs = await getDocs(
        query(colRef, where('configLeagueName', '==', SUPPORTED_LEAGUES[league]), limit(25))
      );
      const teams = docs.docs.map((doc) => doc.data());
      setTeamsList({
        options: teams.map((team: any) => team.appShortname),
        optionsText: teams.map((team: any) => team.name),
      });
    };
    getTeams();
  }, [selectedAbbrv]);

  const handleAddPlayer = useCallback(
    (key: string, value: Option | null) => {
      if (selectedPlayers.options.includes(String(value))) {
        return;
      }
      const index = playersOptions.options.findIndex((o) => o === String(value));
      const text = playersOptions.optionsText[index];
      const updatedSelectedPlayers = {
        options: [...selectedPlayers.options, String(value)],
        optionsText: [...selectedPlayers.optionsText, `${text} (${selectedTeamAbbrv})`],
      };
      setSelectedPlayers(updatedSelectedPlayers);
      onSelectedPlayers && onSelectedPlayers(updatedSelectedPlayers);
    },
    [
      onSelectedPlayers,
      playersOptions.options,
      playersOptions.optionsText,
      selectedPlayers.options,
      selectedPlayers.optionsText,
      selectedTeamAbbrv,
    ]
  );

  const handleDeletePlayer = useCallback(
    (index: number) => {
      const updatedSelectedPlayers = {
        options: selectedPlayers.options.filter((_, i) => i !== index),
        optionsText: selectedPlayers.optionsText.filter((_, i) => i !== index),
      };
      setSelectedPlayers(updatedSelectedPlayers);
      onSelectedPlayers && onSelectedPlayers(updatedSelectedPlayers);
    },
    [onSelectedPlayers, selectedPlayers.options, selectedPlayers.optionsText]
  );

  const handleTeamValue = useCallback((key: string, value: Option | null) => {
    setSelectedTeamAbbrv(value as string);
  }, []);

  return (
    <>
      <SelectWithValidate
        name="team"
        options={teamsList.options}
        optionsText={teamsList.optionsText}
        value={''}
        onValue={handleTeamValue}
        validation={teamSelection}
        label={strings.polls.quizSelectTeamLabel}
        onValidation={onValidation}
        hideValidationError={hideValidationError}
        disabled={teamsList.options.length === 0}
      />
      <SelectWithValidate
        name="player"
        options={playersOptions.options}
        optionsText={playersOptions.optionsText}
        renderValue={() => (
          <Typography color="#000000A0">{strings.polls.quizQuestionSelectMoreLabel}</Typography>
        )}
        onValue={handleAddPlayer}
        validation={playerSelection}
        label={strings.polls.quizSelectPlayerLabel}
        onValidation={onValidation}
        hideValidationError={hideValidationError}
        disabled={squadLoading || playersOptions.options.length === 0}
      />
      {selectedPlayers.optionsText.length > 0 && (
        <Grid container spacing={1} sx={{ paddingBottom: '2px' }}>
          {selectedPlayers.optionsText.map((player, index) => (
            <Grid key={index} item>
              <Chip label={player} onDelete={() => handleDeletePlayer(index)} />
            </Grid>
          ))}
        </Grid>
      )}
    </>
  );
};

export default PollSelectedPlayersLeague;
